/**
 * ClassInsightsPanel — teacher-facing mastery heatmap + at-risk students.
 * Powered by the active ML engine: /api/teacher/insights/class/:class_id.
 * Educational personalisation only — DPDP-compliant.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, Users, BookOpen, ShieldCheck, Sparkles } from 'lucide-react';
import { friendlyError } from '../../lib/errors';
import Loader from '../Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const DARK = '#0C1A12';
const SOFT = '#F0FDF4';

function authHeader() {
  const t = localStorage.getItem('desky_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Mastery → cell colour (lower = redder, higher = greener)
function masteryColor(m) {
  if (m === null || m === undefined) return '#F1F5F9';
  if (m < 0.20) return '#FEE2E2';
  if (m < 0.40) return '#FED7AA';
  if (m < 0.60) return '#FEF08A';
  if (m < 0.80) return '#BBF7D0';
  return '#86EFAC';
}
function masteryText(m) {
  if (m === null || m === undefined) return '—';
  return `${Math.round(m * 100)}`;
}

// Insights is parked behind "Coming soon" for now; flip to false to show the live panel again.
const INSIGHTS_COMING_SOON = true;

function InsightsComingSoon() {
  return (
    <div className="rounded-2xl border border-green-100 bg-white p-10 flex flex-col items-center text-center gap-3" data-testid="ci-coming-soon">
      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: SOFT }}>
        <Activity className="w-7 h-7" style={{ color: GREEN }} />
      </div>
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200">
        <Sparkles className="w-3 h-3 text-slate-400" />
        <span className="text-xs font-semibold text-slate-500 ">Coming soon</span>
      </div>
      <h3 className="text-base font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Class insights</h3>
      <p className="text-sm text-slate-500 max-w-sm">
        We're polishing mastery tracking and at-risk detection for this class. Check back soon.
      </p>
    </div>
  );
}

export default function ClassInsightsPanel(props) {
  return INSIGHTS_COMING_SOON ? <InsightsComingSoon /> : <LiveClassInsights {...props} />;
}

function LiveClassInsights({ classId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!classId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const res = await axios.get(`${API}/teacher/insights/class/${classId}`, { headers: authHeader() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(friendlyError(e, 'Unable to load class insights'));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [classId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="ci-loading">
        <Loader label="Loading class insights…" />
      </div>
    );
  }
  if (err) {
    // Insights isn't reliably available for every class yet — an honest
    // "coming soon" placeholder reads far better than a red error box every
    // time the ML endpoint 404s ("Class not found") for a class it hasn't
    // caught up with.
    return <InsightsComingSoon />;
  }
  if (!data) return null;

  const { heatmap, topics, at_risk, weakest_topics, students_count, class_name } = data;
  const hasData = (heatmap?.length || 0) > 0 && (topics?.length || 0) > 0;

  return (
    <div className="space-y-4" data-testid="class-insights-panel">
      {/* Header card */}
      <div className="rounded-2xl border border-green-100 bg-white p-5 relative overflow-hidden">
        <div aria-hidden className="absolute -top-10 -right-12 w-40 h-40 rounded-full" style={{ background: GREEN, opacity: 0.05 }} />
        <div className="flex items-start gap-3 relative">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: SOFT }}>
            <Activity className="w-5 h-5" style={{ color: GREEN }} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold" style={{ color: DARK, fontFamily: 'Manrope, sans-serif' }}>
              {class_name || 'Class'} — learning insights
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Active ML — mastery + at-risk detection from real student signals. Educational use only (DPDP §10).
            </p>
          </div>
          <div className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-100 px-2.5 py-1 rounded">
            <ShieldCheck className="w-3 h-3"/> No ads · No profiling
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mt-4">
          <Stat icon={Users} label="Students" value={students_count} />
          <Stat icon={BookOpen} label="Topics tracked" value={topics?.length || 0} />
          <Stat icon={AlertTriangle} label="At-risk" value={at_risk?.length || 0} accent={at_risk?.length ? '#DC2626' : GREEN} />
        </div>
      </div>

      {/* Heatmap */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 overflow-hidden" data-testid="ci-heatmap">
        <h4 className="text-sm font-semibold text-slate-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Mastery heatmap</h4>
        <p className="text-xs text-slate-500 mb-4">Higher = better. Numbers are Bayesian mastery (0–100). Click a cell to see signals.</p>
        {!hasData ? (
          <div className="text-center py-8 text-sm text-slate-400">
            No quiz signals yet — once students attempt quizzes, mastery scores will appear here.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="text-xs border-separate border-spacing-0.5">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white text-left px-2 py-1.5 font-semibold text-slate-600 ">Student</th>
                  {topics.map(t => (
                    <th key={t} className="px-1.5 py-1.5 font-semibold text-slate-600 whitespace-nowrap">{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmap.map((row) => (
                  <tr key={row.student_id}>
                    <td className="sticky left-0 bg-white text-slate-700 font-semibold whitespace-nowrap px-2 py-1 max-w-[180px] truncate">
                      {row.student_name}
                    </td>
                    {topics.map(t => (
                      <td key={t} className="text-center font-mono font-semibold rounded px-2 py-1 tabular-nums"
                          style={{ background: masteryColor(row.scores[t]), color: '#15262C', minWidth: 36 }}
                          title={`${row.student_name} · ${t}: ${masteryText(row.scores[t])}/100`}>
                        {masteryText(row.scores[t])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weakest topics + At-risk */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ci-weakest-topics">
          <h4 className="text-sm font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Class-wide weak topics</h4>
          {weakest_topics?.length ? (
            <ul className="space-y-2">
              {weakest_topics.map(w => (
                <li key={w.topic} className="flex items-center gap-3">
                  <div className="text-xs font-semibold text-slate-700 w-40 truncate">{w.topic}</div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${w.avg_mastery * 100}%`, background: masteryColor(w.avg_mastery) }} />
                  </div>
                  <div className="text-xs font-mono font-semibold text-slate-600 tabular-nums w-12 text-right">{(w.avg_mastery * 100).toFixed(0)}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">No topic data yet</p>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ci-at-risk">
          <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <AlertTriangle className="w-4 h-4 text-amber-500"/> At-risk students
          </h4>
          {at_risk?.length ? (
            <ul className="space-y-2">
              {at_risk.map(r => (
                <li key={r.student_id} className="flex items-start gap-3 p-2.5 rounded-lg border border-amber-200 bg-amber-50/40">
                  <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-xs font-semibold text-amber-700 border border-amber-200">
                    {(r.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-800 truncate">{r.name}</div>
                    <div className="text-xs text-slate-600">{r.reason}</div>
                  </div>
                  <span className="text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 flex-shrink-0">
                    {r.risk?.replace('_', ' ')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-400">No students currently at risk — nice work!</p>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-400 text-center pt-1">
        Model: {data.model_version} · Updated live · Educational personalisation only · No third-party sharing
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent = '#34A853' }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 ">
        <Icon className="w-3 h-3" style={{ color: accent }}/> {label}
      </div>
      <div className="text-xl font-semibold tabular-nums mt-0.5" style={{ color: accent, fontFamily: 'Manrope, sans-serif' }}>{value}</div>
    </div>
  );
}
